import React, { FunctionComponent, ReactChild } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollView } from 'react-native'

import styled from 'styled-components/native'

import { AvatarDetails } from '../../atoms/icons/Avatar'
import MessageThread from '../../organisms/messageThread/MessageThread'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { getEmptyMessageContent, tID } from '../../utils/utils'

/**
 * Component is responsible for showing the Messages Page which has
 * a header and a list of message thread contents
 */

export interface Thread {
  conversationId: string
  avatarDetails: AvatarDetails
  threadTitleMsg: string
  threadSubTitleMsg: string
  threadMsg: string
  textBadgeMsg?: string
  timeStampMsg: string
  unreadMsgCount: number
  timeZone: string
  isAICoach?: boolean
  childBadgeLabel?: string
}

export interface MessageThreadListProps {
  messageThreads: Thread[]
  banner?: ReactChild
  onThreadPressed: (conversationId: string) => void
  isSidebarView?: boolean
  selectedConversationId?: string
}

const MessageThreadListContainer = styled.View<{ theme: ThemeType; isSidebarView?: boolean }>(
  ({ theme: { colors, isAuroraTheme }, isSidebarView }) => ({
    backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary),
    flex: 1,
    ...(isSidebarView && {
      backgroundColor: colors.backgroundPrimary,
    }),
  }),
)

export const MessageThreadList: FunctionComponent<MessageThreadListProps> = ({
  messageThreads,
  onThreadPressed,
  banner,
  isSidebarView,
  selectedConversationId,
}) => {
  return messageThreads.length === 0 ? (
    <FormattedMessage
      defaultMessage='No conversations or messages retrieved'
      description='Text describing that there is no conversations or messages retrieved'
    />
  ) : (
    <MessageThreadListContainer isSidebarView={isSidebarView}>
      {banner}
      <ScrollView testID={tID('MessageThreadList')}>
        {messageThreads.map((thread) => {
          const {
            conversationId,
            avatarDetails,
            threadMsg,
            threadSubTitleMsg,
            threadTitleMsg,
            unreadMsgCount,
            timeStampMsg,
            textBadgeMsg,
            isAICoach,
            childBadgeLabel,
          } = thread
          return (
            <MessageThread
              key={conversationId}
              avatarDetails={avatarDetails}
              threadTitleMsg={threadTitleMsg}
              threadSubTitleMsg={threadSubTitleMsg}
              threadMsg={threadMsg || getEmptyMessageContent(threadTitleMsg)}
              textBadgeMsg={textBadgeMsg}
              conversationId={conversationId}
              unreadMsgCount={unreadMsgCount}
              timeStampMsg={timeStampMsg}
              onThreadPressed={onThreadPressed}
              isAICoach={isAICoach}
              childBadgeLabel={childBadgeLabel}
              {...(isSidebarView && { isSidebarView: true, isSelected: conversationId === selectedConversationId })}
            />
          )
        })}
      </ScrollView>
    </MessageThreadListContainer>
  )
}
